import React from 'react';
import { Button, Icon, Select } from '@gerdau/hefesto';
import { Table, Tag, Stepper, KeyValueItem } from '@gerdau/hefesto-mais-web';
import {
  DEFAULT_WIRE_ITEM,
  EProvideWireQuantityTypes,
  IAnnealedWire,
  IExtraItensState
} from '../../../../../store/ducks/fabrication-purchase-order/types';
import {
  ProvideQuantityByContainer,
  WireItensTableSpan,
  TableContainer,
  WireItensTableContainer
} from './styles';
import {
  IAnnealedWireType,
  IAnnealedWireTypesState
} from '../../../../../store/ducks/fabrication/types';

interface IWireItensTableProps {
  extraItensState: IExtraItensState;
  setExtraItensState(extraItensState: IExtraItensState): void;
  wireTypesState: IAnnealedWireTypesState;
}

const WireItensTable: React.FC<IWireItensTableProps> = ({
  extraItensState,
  setExtraItensState,
  wireTypesState
}) => {
  const dataSource = extraItensState.annealedWires.map((item, idx) => ({
    ...item,
    key: idx
  }));

  const onRemoveWireItem = (data: IAnnealedWire) => {
    const newData: IAnnealedWire[] = dataSource.filter(
      (item) => item.key !== data.key
    );

    if (!newData.length) {
      return setExtraItensState({
        ...extraItensState,
        annealedWires: [DEFAULT_WIRE_ITEM]
      });
    }
    setExtraItensState({
      ...extraItensState,
      annealedWires: newData
    });
  };

  const onSelectWeightOrPercentage = (
    data: IAnnealedWire,
    key: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any
  ) => {
    const newData: IAnnealedWire[] = dataSource.map((item) =>
      item.key === data.key ? { ...item, [key]: value } : item
    );

    setExtraItensState({
      ...extraItensState,
      annealedWires: newData
    });
  };

  const onClickProvideQuantityBy = (
    data: IAnnealedWire,
    key: string,
    value: string,
    keyToReset: string
  ) => {
    const newData: IAnnealedWire[] = dataSource.map((item) =>
      item.key === data.key ? { ...item, [key]: value, [keyToReset]: '' } : item
    );

    setExtraItensState({
      ...extraItensState,
      annealedWires: newData
    });
  };

  const getWireMultipleWeightInfo = (wireTypeId?: string): string => {
    const wireType = wireTypesState?.wireTypes?.find(
      (wt) => wt.id === wireTypeId
    );

    if (wireType?.description.toLowerCase().includes('bwg 12')) {
      return 'Para BWG 12, procure informar quantidades múltiplas de 70kg.';
    } else if (wireType?.description.toLowerCase().includes('bwg 16')) {
      return 'Para BWG 16, procure informar quantidades múltiplas de 50kg ou 70kg.';
    } else if (wireType?.description.toLowerCase().includes('bwg 18 torcido')) {
      return 'Para BWG 18 torcido, procure informar quantidades múltiplas de 1kg ou 8kg.';
    } else if (wireType?.description.toLowerCase().includes('bwg 18 liso')) {
      return 'Para BWG 18 liso, procure informar quantidades múltiplas de 35kg ou 50kg.';
    } else {
      return '';
    }
  };

  const getColumns = () => {
    const columns = [
      {
        title: 'Tipo de arame',
        dataIndex: '',
        render: (data: IAnnealedWire) => (
          <Select
            name="Select"
            label=""
            value={data.annealedWireTypeId}
            options={
              wireTypesState.wireTypes?.map((wireItem: IAnnealedWireType) => ({
                value: wireItem.id,
                label: wireItem.description
              })) || []
            }
            disabled={
              !wireTypesState.wireTypes ||
              (wireTypesState.wireTypes &&
                wireTypesState.wireTypes.length === 0)
            }
            onSelect={(e) =>
              onSelectWeightOrPercentage(data, 'annealedWireTypeId', e)
            }
          />
        )
      },
      {
        title: 'Fornecer quantidade por',
        dataIndex: '',
        render: (data: IAnnealedWire) => (
          <>
            <ProvideQuantityByContainer>
              <Tag
                variant={
                  data.provideQuantityBy == 'WEIGHT' && data.annealedWireTypeId
                    ? 'primary'
                    : 'light'
                }
              >
                <WireItensTableSpan
                  $disabled={Boolean(!data.annealedWireTypeId)}
                  onClick={() =>
                    data.annealedWireTypeId
                      ? onClickProvideQuantityBy(
                          data,
                          'provideQuantityBy',
                          'WEIGHT',
                          'percentage'
                        )
                      : undefined
                  }
                >
                  Peso do arame
                </WireItensTableSpan>
              </Tag>
              <Tag
                variant={
                  data.provideQuantityBy == 'PERCENTAGE' &&
                  data.annealedWireTypeId
                    ? 'primary'
                    : 'light'
                }
              >
                <WireItensTableSpan
                  $disabled={Boolean(!data.annealedWireTypeId)}
                  data-testid="percentage-tag"
                  onClick={() =>
                    data.annealedWireTypeId
                      ? onClickProvideQuantityBy(
                          data,
                          'provideQuantityBy',
                          'PERCENTAGE',
                          'weight'
                        )
                      : undefined
                  }
                >
                  Porcentagem
                </WireItensTableSpan>
              </Tag>
            </ProvideQuantityByContainer>
          </>
        )
      },
      {
        title: (
          <WireItensTableContainer>
            <span>Peso do arame (em kg) </span>
          </WireItensTableContainer>
        ),
        dataIndex: '',
        render: (data: IAnnealedWire) => (
          <WireItensTableContainer className="wire-weight">
            <Stepper
              label=""
              name={'weightInput'}
              disabled={
                data.provideQuantityBy ==
                  EProvideWireQuantityTypes.PERCENTAGE ||
                (wireTypesState.wireTypes &&
                  wireTypesState.wireTypes.length === 0) ||
                !data.annealedWireTypeId
              }
              value={data.weight}
              onChange={(e) =>
                onSelectWeightOrPercentage(data, 'weight', e.target.value)
              }
              minLength={0}
            />
            {data.provideQuantityBy == EProvideWireQuantityTypes.WEIGHT &&
              data.annealedWireTypeId && (
                <KeyValueItem
                  title=""
                  helperText={getWireMultipleWeightInfo(
                    data.annealedWireTypeId
                  )}
                  children={<></>}
                />
              )}
          </WireItensTableContainer>
        )
      },
      {
        title: (
          <WireItensTableContainer>
            <span>Porcentagem </span>
          </WireItensTableContainer>
        ),
        dataIndex: '',
        render: (data: IAnnealedWire) => (
          <Stepper
            label=""
            name={'percentageInput'}
            disabled={
              data.provideQuantityBy == EProvideWireQuantityTypes.WEIGHT ||
              !wireTypesState.wireTypes?.length ||
              !data.annealedWireTypeId
            }
            value={data.percentage}
            onChange={(e) =>
              onSelectWeightOrPercentage(data, 'percentage', e.target.value)
            }
            minLength={0}
          />
        )
      },
      {
        title: '',
        dataIndex: '',
        render: (data: IAnnealedWire) => (
          <Button
            name="deleteButton"
            variant="neutral"
            disabled={!wireTypesState.wireTypes?.length}
            icon={<Icon option="Trash" variant="danger" />}
            onClick={() => onRemoveWireItem(data)}
          />
        )
      }
    ];

    return columns;
  };

  return (
    <TableContainer>
      <Table columns={getColumns()} dataSource={dataSource} />
    </TableContainer>
  );
};

export default WireItensTable;
